import { Helmet } from '@dr.pogodin/react-helmet';
import { PlusIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { useSelectedCompany } from 'src/contexts';
import { useMyUserClient } from 'src/hooks';
import { capitalize } from 'src/util';

import { Api, useApiCallable } from '../../api';
import {
  Button,
  ConfirmDeleteModal,
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Tabs,
  Text,
} from '../../components';
import { Color, Style } from '../../constants';
import {
  AccountCard,
  AddAccountModal,
  DeploymentCard,
  InstructionsModal,
  NewDeploymentModal,
} from './components';
import { deploymentPlatform } from './components/DeploymentCard';

type Account = Api.Environment.V['accounts'][number];
type Deployment = Api.Environment.V['deployments'][number];

export default function EnvironmentsPage() {
  const company = useSelectedCompany();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const myUser = useMyUserClient();
  const canManage = !!myUser.agencyRole;
  const api = Api.Environment.useReadCollectionListCompanySub(company.id);

  const [deploymentModal, setDeploymentModal] = React.useState<{
    show: boolean;
    existing: Deployment | null;
  }>({ show: false, existing: null });
  const [accountModal, setAccountModal] = React.useState<{
    show: boolean;
    existing: Account | null;
  }>({ show: false, existing: null });
  const [instructionsAccount, setInstructionsAccount] =
    React.useState<Account | null>(null);
  const [confirmDeleteAccount, setConfirmDeleteAccount] =
    React.useState<Account | null>(null);
  const [confirmDeleteDeployment, setConfirmDeleteDeployment] =
    React.useState<Deployment | null>(null);
  const callApi = useApiCallable();

  if (api.error) return <PageError {...api} />;
  if (!api.data) return <PageLoading />;

  const environments = api.data['hydra:member'];
  // Active environment lives in the URL (?env=slug) so the tabs add history
  // entries and the page is shareable — like the Open/Closed ticket tabs.
  const searchParams = new URLSearchParams(search);
  const activeSlug = searchParams.get('env') ?? environments[0]?.slug ?? null;
  const activeEnv = environments.find((e) => e.slug === activeSlug);

  // URL deployments (web / admin) above app deployments (M10C-QA-006).
  const allDeployments = activeEnv?.deployments ?? [];
  const deployments = [
    ...allDeployments.filter((d) => !deploymentPlatform(d)),
    ...allDeployments.filter((d) => deploymentPlatform(d)),
  ];

  const tabs = environments.map((env) => ({
    key: env.slug,
    label: capitalize(env.slug),
    selected: env.slug === activeSlug,
    onClick: () => navigate(`${pathname}?env=${env.slug}`),
  }));

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Environments | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
          <html.div>
            <Text typeface="body.medium" color="neutral100">
              Development
            </Text>
            <Text as="h1" typeface="headline" color="neutral100">
              Environments
            </Text>
          </html.div>
          {canManage && environments.length > 0 && (
            <Button
              label="New Deployment"
              iconLeft={(p) => <PlusIcon {...p} />}
              onClick={() => setDeploymentModal({ show: true, existing: null })}
            />
          )}
        </html.div>

        {environments.length > 0 && (
          <html.div style={styles.tabBar}>
            <Tabs tabs={tabs} accentColor={Color.accent} />
          </html.div>
        )}

        {environments.length === 0 && (
          <EmptyState
            title="No Environments"
            subtitle="Environments will be shown here."
          />
        )}

        {activeEnv && (
          <>
            {deployments.length > 0 && (
              <html.div
                style={
                  canManage ? styles.deploymentList : styles.deploymentGrid
                }
              >
                {deployments.map((dep) => (
                  <DeploymentCard
                    key={dep.id}
                    deployment={dep}
                    companySlug={company.slug}
                    canManage={canManage}
                    onEdit={() =>
                      setDeploymentModal({ show: true, existing: dep })
                    }
                    onDelete={() => setConfirmDeleteDeployment(dep)}
                    onViewCommitHistory={() =>
                      navigate(
                        `/company/${company.slug}/development/environments/${dep.id}/commits`
                      )
                    }
                  />
                ))}
              </html.div>
            )}

            {(activeEnv.accounts.length > 0 || canManage) && (
              <Stack gap={1}>
                <html.div
                  style={[Style.row, Style.alignCenter, Style.spaceBetween]}
                >
                  <Text as="h2" typeface="body.medium" color="neutral100">
                    Accounts
                  </Text>
                  {canManage && (
                    <html.button
                      style={styles.addAccount}
                      onClick={() =>
                        setAccountModal({ show: true, existing: null })
                      }
                    >
                      <PlusIcon size={18} color={Color.accent} />
                      <Text typeface="body.medium" color="accent">
                        Add Account
                      </Text>
                    </html.button>
                  )}
                </html.div>

                {activeEnv.accounts.length > 0 && (
                  <html.div style={styles.accountsGrid}>
                    {activeEnv.accounts.map((acc) => (
                      <AccountCard
                        key={acc.id}
                        account={acc}
                        canManage={canManage}
                        onEdit={() =>
                          setAccountModal({ show: true, existing: acc })
                        }
                        onDelete={() => setConfirmDeleteAccount(acc)}
                        onViewInstructions={() => setInstructionsAccount(acc)}
                      />
                    ))}
                  </html.div>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>

      {deploymentModal.show && (
        <NewDeploymentModal
          environments={environments}
          existing={deploymentModal.existing}
          defaultEnvironmentIri={activeEnv?.['@id']}
          onClose={() => setDeploymentModal({ show: false, existing: null })}
        />
      )}

      {accountModal.show && (
        <AddAccountModal
          environments={environments}
          existing={accountModal.existing}
          defaultEnvironmentIri={activeEnv?.['@id']}
          onClose={() => setAccountModal({ show: false, existing: null })}
        />
      )}

      {instructionsAccount && (
        <InstructionsModal
          instructions={instructionsAccount.instructions ?? ''}
          canManage={canManage}
          onClose={() => setInstructionsAccount(null)}
          onEdit={() => {
            setAccountModal({ show: true, existing: instructionsAccount });
            setInstructionsAccount(null);
          }}
          onDelete={async () => {
            await callApi(`/environment-accounts/${instructionsAccount.id}`, {
              method: 'PATCH',
              jsonBody: { instructions: null },
            });
            setInstructionsAccount(null);
            api.invalidate();
          }}
        />
      )}

      {confirmDeleteAccount && (
        <ConfirmDeleteModal
          title={`Delete ${confirmDeleteAccount.type}`}
          message="Are you sure you want to delete this account? This action can’t be undone."
          onConfirm={async () => {
            await callApi(`/environment-accounts/${confirmDeleteAccount.id}`, {
              method: 'DELETE',
            });
            setConfirmDeleteAccount(null);
            api.invalidate();
          }}
          onClose={() => setConfirmDeleteAccount(null)}
        />
      )}

      {confirmDeleteDeployment && (
        <ConfirmDeleteModal
          title={`Delete ${confirmDeleteDeployment.name}`}
          message="Are you sure you want to delete this deployment? This action can’t be undone."
          onConfirm={async () => {
            await callApi(
              `/environment-deployments/${confirmDeleteDeployment.id}`,
              { method: 'DELETE' }
            );
            setConfirmDeleteDeployment(null);
            api.invalidate();
          }}
          onClose={() => setConfirmDeleteDeployment(null)}
        />
      )}
    </PageWrap>
  );
}

const styles = css.create({
  tabBar: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  deploymentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  deploymentGrid: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'repeat(2, minmax(0, 1fr))',
      '@media (max-width: 768px)': '1fr',
    },
    gap: 16,
  },
  accountsGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fill, minmax(max(min(300px, 100%), calc((100% - 32px) / 3)), 1fr))',
    gap: 16,
  },
  addAccount: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
});
